import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, CheckCircle2, Circle, FolderArchive, Link2, Lock, Plus, Unlink, Loader2 } from "lucide-react";
import { CanaleBadge } from "@/pages/trattative/Trattative";
import { ContattiBox, SupportoBox, InformativaRegBox } from "@/pages/trattative/TrattativaConsensi";
import { VideoBox } from "@/pages/trattative/video/VideoBox";
import { RecessoBox } from "@/pages/trattative/RecessoBox";
import { IncassoBox } from "@/pages/trattative/IncassoBox";
import { QuestionarioBox, CopiaBox } from "@/pages/trattative/QuestionarioBox";
import { RuntsBadge } from "@/components/RuntsBadge";
import { AdesioneCard } from "@/pages/trattative/AdesioneCard";
import { DateTimeInputIT } from "@/components/DateInputIT";

const dt = (v) => (v ? new Date(v).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const STATO_MUP = { bozza: "Bozza", pronto_invio: "Pronto all'invio", inviato: "Inviato", firmato: "Firmato", annullato: "Annullato" };

const META = { canale: "trt-box-protocollo", supporto: "trt-consensi-box", info_registrazione: "trt-consensi-box", registrazione: "trt-box-video",
  precontrattuale: "trt-box-mup", ricezione: "trt-box-mup", firma: "trt-box-mup" };
const vai = (t, key) => document.getElementById(t.adesione && ["precontrattuale", "ricezione", "firma"].includes(key) ? "trt-box-adesione" : META[key])?.scrollIntoView({ behavior: "smooth", block: "start" });

function RigaPasso({ t, p, onDone }) {
  const [nota, setNota] = useState(null);
  const presenza = async () => {
    try { const { data } = await api.post(`/trattative/${t.id}/forza`, { passo: p.key, motivo: nota }); onDone(data); setNota(null); toast.success("Registrato come svolto in presenza"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (<div>
    <div className={`flex items-start gap-2.5 text-sm rounded-md px-1.5 py-0.5 ${p.corrente ? "bg-teal-50" : ""}`} data-testid={`trt-passo-${p.key}`}>
      {p.fatto ? <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${p.forzato ? "text-amber-500" : "text-emerald-600"}`} /> : p.bloccato ? <Lock className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" /> : <Circle className={`h-4 w-4 mt-0.5 shrink-0 ${p.corrente ? "text-teal-600" : "text-slate-300"}`} />}
      <span className={`flex-1 ${p.fatto ? "text-slate-800" : p.bloccato ? "text-slate-400" : "text-slate-700"} ${p.corrente ? "font-medium text-teal-900" : ""}`}>{p.label}
        {p.facoltativo && !p.fatto && <span className="ml-1.5 text-[10px] text-slate-400" data-testid={`trt-passo-facoltativo-${p.key}`}>· facoltativo</span>}
        {p.bloccato && <span className="block text-[11px] text-slate-400" data-testid={`trt-passo-manca-${p.key}`}>Prima serve: {p.manca.join("; ")}</span>}
        {p.forzato && <span className="block text-[11px] text-amber-700" data-testid={`trt-passo-forzato-${p.key}`}>svolto in presenza · {p.forzato.by}: {p.forzato.motivo}</span>}
        {p.nota && <span className="block text-[11px] text-teal-700" data-testid={`trt-passo-nota-${p.key}`}>{p.nota}</span>}</span>
      {!p.fatto && !p.bloccato && !p.facoltativo && t.stato === "aperta" && nota === null && <button onClick={() => setNota("")} className="text-[11px] text-teal-700 hover:underline shrink-0" data-testid={`trt-forza-${p.key}`}>fatto in presenza</button>}
    </div>
    {nota !== null && <div className="flex gap-2 ml-7 mt-1" data-testid={`trt-presenza-box-${p.key}`}>
      <input autoFocus value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota: dove, come, chi (almeno 10 caratteri)" className="flex-1 h-8 border border-slate-300 rounded-md px-2 text-xs" data-testid={`trt-presenza-nota-${p.key}`} />
      <Button size="sm" className="h-8" disabled={nota.trim().length < 10} onClick={presenza} data-testid={`trt-presenza-conferma-${p.key}`}>Registra</Button>
      <Button size="sm" variant="ghost" className="h-8" onClick={() => setNota(null)}>Annulla</Button></div>}
  </div>);
}

function Passi({ t, onDone }) {
  const corr = t.passi.find((p) => p.corrente);
  const fac = t.passi.filter((p) => !p.fase);
  return (
    <div className="space-y-3" data-testid="trt-passi">
      <div className="flex gap-1.5" data-testid="trt-fasi">{t.fasi.map((f, i) => (
        <div key={f.key} className={`flex-1 rounded-md px-2 py-1.5 text-[11px] leading-tight border ${f.fatta ? "bg-emerald-50 border-emerald-200 text-emerald-800" : f.corrente ? "bg-teal-50 border-teal-300 text-teal-900 font-medium" : "border-slate-200 text-slate-500"}`} data-testid={`trt-fase-${f.key}`}>
          <span className="block text-[10px] opacity-70">{i + 1}</span>{f.label}</div>))}</div>
      {corr && t.stato === "aperta" && <Button size="sm" className="w-full brand-gradient text-white border-0" onClick={() => vai(t, corr.key)} data-testid="trt-prossima-azione">Prossima azione: {corr.label}</Button>}
      {t.fasi.map((f) => (<div key={f.key} className="space-y-1"><div className="text-[11px] uppercase tracking-wide text-slate-400">{f.label}</div>
        {t.passi.filter((p) => p.fase === f.key).map((p) => <RigaPasso key={p.key} t={t} p={p} onDone={onDone} />)}</div>))}
      {!!fac.length && <div className="space-y-1"><div className="text-[11px] uppercase tracking-wide text-slate-400">Dopo la firma (facoltativi)</div>
        {fac.map((p) => <RigaPasso key={p.key} t={t} p={p} onDone={onDone} />)}</div>}
    </div>
  );
}

function CanaleDialog({ t, onDone }) {
  const [open, setOpen] = useState(false);
  const [modo, setModo] = useState("cambio");
  const [canale, setCanale] = useState(t.canale);
  const [motivo, setMotivo] = useState("");
  const [app, setApp] = useState("");
  const salva = async () => {
    try {
      const { data } = modo === "rifiuto"
        ? await api.post(`/trattative/${t.id}/rifiuto-distanza`, { motivo, appuntamento_at: app ? new Date(app).toISOString() : "" })
        : await api.post(`/trattative/${t.id}/canale`, { canale, motivo });
      toast.success("Canale aggiornato"); setOpen(false); setMotivo(""); onDone(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <>
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => { setModo("cambio"); setOpen(true); }} data-testid="trt-cambia-canale">Cambia canale</Button>
        {t.canale !== "presenza" && <Button variant="outline" size="sm" onClick={() => { setModo("rifiuto"); setOpen(true); }} data-testid="trt-rifiuto-distanza">Il cliente non accetta la distanza</Button>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="trt-canale-dialog">
          <DialogHeader><DialogTitle>{modo === "rifiuto" ? "Passaggio alla vendita in presenza" : "Cambio del canale di vendita"}</DialogTitle></DialogHeader>
          {modo === "cambio" ? (
            <div className="space-y-1.5"><Label>Nuovo canale</Label>
              <select value={canale} onChange={(e) => setCanale(e.target.value)} className="w-full h-9 text-sm border border-slate-200 rounded-md px-2" data-testid="trt-canale-select">
                <option value="presenza">In presenza</option><option value="mista">Mista</option><option value="distanza">Interamente a distanza</option>
              </select>
              <p className="text-[11px] text-slate-500">Se il canale passa verso la distanza, i passaggi richiesti in più vengono aggiunti automaticamente.</p>
            </div>
          ) : (
            <div className="space-y-1.5"><Label>Appuntamento in agenzia (facoltativo)</Label>
              <DateTimeInputIT value={app} onChange={(v) => setApp(v)} testid="trt-appuntamento" /></div>
          )}
          <div className="space-y-1.5"><Label>Motivo {modo === "cambio" && "(obbligatorio)"}</Label>
            <Textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="trt-canale-motivo" /></div>
          <DialogFooter><Button onClick={salva} disabled={modo === "cambio" && (!motivo.trim() || canale === t.canale)} className="brand-gradient text-white border-0" data-testid="trt-canale-salva">Conferma</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MupBox({ t, onDone }) {
  const nav = useNavigate();
  const [disp, setDisp] = useState([]);
  useEffect(() => { api.get(`/trattative/${t.id}/mup-disponibili`).then(({ data }) => setDisp(data)).catch(() => {}); }, [t.id, t.mup.length]);
  const collega = async (pid) => { try { const { data } = await api.post(`/trattative/${t.id}/mup`, { pratica_id: pid }); onDone(data); toast.success("MUP collegato"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const precompila = async (pid) => {
    try { const { data } = await api.post(`/trattative/${t.id}/mup/${pid}/precompila-griglia`); onDone(data.trattativa); toast.success(`Griglia precompilata: ${data.spuntate} voci spuntate in «Richieste ed esigenze». Verifichi nel MUP.`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const scollega = async (pid) => { if (!window.confirm("Scollegare il MUP dalla trattativa?")) return; const { data } = await api.delete(`/trattative/${t.id}/mup/${pid}`); onDone(data); };
  const nuovo = `/mup/nuova?nome=${encodeURIComponent(t.cliente.nome)}${t.cliente.anagrafica_id ? `&ente=${t.cliente.anagrafica_id}` : ""}${t.prodotto_nome ? `&prodotto=${encodeURIComponent(t.prodotto_nome)}` : ""}&trattativa=${t.id}`;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="trt-mup-box">
      <div className="flex items-center justify-between mb-3"><h3 className="font-semibold text-slate-900">MUP e precontrattuale</h3>
        <Button size="sm" onClick={() => nav(nuovo)} className="brand-gradient text-white border-0" data-testid="trt-nuovo-mup"><Plus className="h-4 w-4 mr-1" /> Nuovo MUP</Button></div>
      {t.mup.length === 0 && <p className="text-sm text-slate-400 mb-2">Nessun MUP collegato.</p>}
      {t.mup.map((m) => (
        <div key={m.pratica_id} className="flex items-center justify-between border rounded-lg px-3 py-2 mb-2 text-sm" data-testid={`trt-mup-${m.pratica_id}`}>
          <button onClick={() => nav(`/mup/pratiche/${m.pratica_id}`)} className="text-left hover:underline"><b>{m.code}</b> · {m.product_name || "—"} · {STATO_MUP[m.state] || m.state}{m.signed_at && ` · firmato ${dt(m.signed_at)}${m.modalita_firma === "link_cliente" ? " (a distanza)" : ""}`}</button>
          {t.questionario?.confermato_at && !m.signed_at && !["inviato", "firmato"].includes(m.state) && <Button variant="outline" size="sm" className="h-7 text-xs ml-auto mr-1" onClick={() => precompila(m.pratica_id)} data-testid={`trt-precompila-${m.pratica_id}`}>Precompila griglia dal questionario</Button>}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => scollega(m.pratica_id)} data-testid={`trt-scollega-${m.pratica_id}`}><Unlink className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      {disp.length > 0 && (
        <div className="mt-3"><p className="text-xs text-slate-500 mb-1">MUP esistenti dello stesso cliente:</p>
          {disp.map((m) => (
            <div key={m.pratica_id} className="flex items-center justify-between text-xs py-1" data-testid={`trt-mup-disp-${m.pratica_id}`}>
              <span>{m.code} · {m.product_name || "—"} · {STATO_MUP[m.state] || m.state}</span>
              <Button variant="outline" size="sm" className="h-7" onClick={() => collega(m.pratica_id)} data-testid={`trt-collega-${m.pratica_id}`}><Link2 className="h-3 w-3 mr-1" /> Collega</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrattativaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/trattative/${id}`).then(({ data }) => setT(data)).catch(() => toast.error("Trattativa non trovata")), [id]);
  useEffect(() => { load(); }, [load]);
  const stato = async (s) => {
    const motivo = s === "annullata" ? window.prompt("Motivo dell'annullamento") : "";
    if (s === "annullata" && !motivo) return;
    try { const { data } = await api.post(`/trattative/${id}/stato`, { stato: s, motivo }); setT(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const fascicolo = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/trattative/${id}/fascicolo`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `Fascicolo_${t.codice}.zip`; a.click();
      load();
    } catch { toast.error("Impossibile generare il fascicolo"); } finally { setBusy(false); }
  };
  if (!t) return <div className="p-10 text-slate-400">Caricamento…</div>;
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <button onClick={() => nav("/trattative")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-4" data-testid="trt-detail-back"><ArrowLeft className="h-4 w-4" /> Trattative</button>
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-xs text-slate-500">{t.codice} · {t.stato_label}</div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="trt-detail-cliente">{t.cliente.nome}</h1>
            <RuntsBadge cf={t.cliente.codice_fiscale || t.cliente.partita_iva} testid="trt-runts-badge" />
            <div className="flex items-center gap-2 mt-2 flex-wrap"><CanaleBadge canale={t.canale} label={t.canale_label} />
              <span className="text-xs text-slate-500">{t.prodotto_nome || "prodotto da definire"}{t.origine_contatto && ` · contatto: ${t.origine_contatto}`}</span></div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={fascicolo} disabled={busy} data-testid="trt-fascicolo">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderArchive className="h-4 w-4 mr-2" />} Fascicolo di vendita</Button>
            {t.stato === "aperta" ? (<>
              <Button variant="outline" onClick={() => stato("conclusa")} data-testid="trt-concludi">Concludi</Button>
              <Button variant="ghost" className="text-red-600" onClick={() => stato("annullata")} data-testid="trt-annulla">Annulla</Button>
            </>) : <Button variant="outline" onClick={() => stato("aperta")} data-testid="trt-riapri">Riapri</Button>}
          </div>
        </div>
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-5">
          <div className="space-y-5">
            <div id="trt-box-protocollo" className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3"><h3 className="font-semibold text-slate-900">Percorso della trattativa</h3>
                <span className="text-xs text-slate-500" data-testid="trt-passi-conteggio">{t.passi_completati}/{t.passi_obbligatori} completati</span></div>
              <Passi t={t} onDone={setT} />
              <div className="mt-4 pt-4 border-t"><CanaleDialog t={t} onDone={setT} /></div>
              {t.appuntamento_at && <p className="text-xs text-slate-600 mt-2" data-testid="trt-appuntamento-info">Appuntamento in agenzia: {dt(t.appuntamento_at)}</p>}
            </div>
            <div id="trt-consensi-box" className="bg-white rounded-xl border border-slate-200 p-5 space-y-5" data-testid="trt-consensi-box">
              <h3 className="font-semibold text-slate-900">Cliente e consensi</h3>
              {t.stato !== "aperta" && <p className="text-xs text-slate-500" data-testid="trt-consensi-chiusa">Trattativa {t.stato_label.toLowerCase()}: dati in sola lettura. Per modificarli, riapra la trattativa.</p>}
              <fieldset disabled={t.stato !== "aperta"} className="space-y-5 disabled:opacity-70">
              <ContattiBox key={`${t.cliente.email}|${t.cliente.cellulare}`} t={t} onDone={setT} />
              <div className="pt-4 border-t"><p className="text-sm font-medium text-slate-800 mb-2">Supporto della documentazione{t.canale !== "presenza" && <span className="text-red-600"> *</span>}</p><SupportoBox t={t} onDone={setT} /></div>
              {t.canale !== "presenza" && <div className="pt-4 border-t"><InformativaRegBox t={t} onReload={load} /></div>}
              </fieldset>
            </div>
            <div id="trt-box-adesione"><AdesioneCard a={t.adesione} /></div>
            <CopiaBox t={t} onDone={setT} />
            <QuestionarioBox t={t} onDone={setT} />
            {t.canale !== "presenza" && <div id="trt-box-video"><VideoBox t={t} onReload={load} /></div>}
            <div id="trt-box-mup"><MupBox t={t} onDone={setT} /></div>
            <IncassoBox t={t} onDone={setT} />
            {t.canale === "distanza" && <RecessoBox t={t} onDone={setT} onReload={load} />}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="trt-registro">
            <h3 className="font-semibold text-slate-900 mb-3">Registro unico degli eventi</h3>
            <div className="space-y-2.5 max-h-[640px] overflow-y-auto pr-1">
              {[...t.registro].reverse().map((e, i) => (
                <div key={i} className="text-sm border-l-2 border-teal-200 pl-3" data-testid={`trt-evento-${i}`}>
                  <div className="text-[11px] text-slate-400">{dt(e.at)} · {e.fonte} · {e.by}</div>
                  <div className="text-slate-700"><span className="font-medium">{e.tipo_label}</span>{e.dettaglio && ` — ${e.dettaglio}`}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
